import { IncomingHttpHeaders } from "http";
import { ParsedUrlQuery } from "querystring";

import { PartnerType } from "@tm/isomorphic-utils";

export interface IPageRequestParams {
  partner: PartnerType;
  headers?: IncomingHttpHeaders;
  query?: ParsedUrlQuery;
  cmp?: string | undefined;
  dvid?: string;
  lob?: string;
}

export interface IListingRequestParams extends IPageRequestParams {
  userSearchIntent: {
    country: string[];
    departureDateTime: number;
    arrivalDateTime: number;
    travelPurpose: string;
    numberOfTravellers: number;
    segment: string;
    tripOption?: string;
    planType?: string;
    callerSource?: string;
  };
  isV2?: boolean;
}

export interface IGetNextPageRouteRequestParams extends IPageRequestParams {
  userSearchIntent: {
    country: string[];
    departureDateTime: number;
    arrivalDateTime: number;
    numberOfTravellers: number;
    segment: string;
  };
  isV2Endpoint?: boolean;
}
export interface IGetSuggestionsInfoRequestParams extends IPageRequestParams {
  pattern: string;
  pageSize: number;
}

export interface ICheckCheckoutEligibilityRequestParams
  extends IPageRequestParams {
  phone: string;
  email: string;
}

export interface IAddPassportRequestParams extends IPageRequestParams {
  bookingId: string;
  pageId: string;
}

export interface IOrderConfirmedRequestParams extends IPageRequestParams {
  bookingId: string;
  itineraryId?: string;
}

export interface IOrderDetailsRequestParams extends IPageRequestParams {
  bookingId: string;
}

export interface IClaimLandingRequestParams extends IPageRequestParams {
  bookingId: string;
}

export interface IClaimBenefitRequestParams {
  bookingId: string;
  categoryId?: string;
  nodeId?: string;
  currency?: string;
  partner?: string;
  query?: Record<string, string | string[] | undefined>;
}

export interface IEditTravellersRequestParams extends IPageRequestParams {
  bookingID: string | null;
  data?: {
    refId?: string;
    FullName?: string;
    Dob?: string;
    Gender?: string;
    PassportNo?: string;
  }[];
}

export type EditTravellerSubject = {
  subject_id: string;
  new_pax_data: Record<string, unknown>;
};

export type EditTravellerPayload = {
  booking_id: string;
  subjects: EditTravellerSubject[];
};

export type EndorsementComponentData = {
  title?: string;
  icon?: string;
  subtitle?: string;
  desc?: string;
  description?: string;
  followUpMessage?: string;
  newPolicyUrl?: string;
  cta?: {
    type?: string;
    label?: string;
    actionId?: string;
    isEnabled?: boolean;
    isVisible?: boolean;
  };
};

export type SubjectError = {
  displayError: string;
  errorField: string;
};

export type EndorsementResponse = {
  success: boolean;
  message?: string;
  data?: {
    components?: Array<{
      id: string;
      data: EndorsementComponentData;
    }>;
    endorsementStatus?: string;
    subjectErrors?: Record<string, SubjectError>;
  };
};

export interface ISuccessResponse<T = any> {
  responseStatus: "SUCCESS";
  data?: T;
  error?: null;
}

export interface IErrorResponse {
  responseStatus: "ERROR";
  error: {
    StatusCode?: number;
    code?: string;
    message?: {
      title?: string;
      decsription?: string;
      icon?: string;
    };
  };
}

export type ApiResponseType<T = any> = ISuccessResponse<T> | IErrorResponse;

interface Styles {
  color?: string;
  "font-size"?: string;
  mobile?: {
    "font-size"?: string;
  };
}

interface TextWithStyles {
  icon?: string;
  value?: string;
  styles?: Styles;
}

interface TextNodeType {
  text?: string | TextWithStyles | TextWithStyles[];
}

export interface IBenefits {
  id?: string;
  icon?: string;
  title?: TextNodeType;
  info?: TextNodeType;
  price?: TextNodeType;
}

export interface CardDataInterface {
  csrfToken?: string;
  productId: string;
  quoteId: string;
  productType?: string;
  insPlanName?: TextNodeType;
  packName?: TextNodeType;
  vendorDisplayIcon?: string;
  comparePlans?: TextNodeType;
  paxPerDay?: TextNodeType;
  insAmount?: TextNodeType;
  insIcon?: string;
  hasTravelAssistant?: boolean;
  provideCashlessClaim?: boolean;
  insuranceType?: TextNodeType;
  planExtraInfo?: TextNodeType;
  buyBtn?: string;
  originalPrice?: TextNodeType;
  discountText?: TextNodeType;
  selectedForCompare?: boolean;
  vendorDisplayPrefix?: string;
  insurerDetails?: {
    insurer: TextNodeType;
    details: TextNodeType;
  };
  insurerNumber?: TextNodeType;
  ctaLink?: string | TextNodeType;
  tncLink?: string;
  isPopular?: boolean;
  popularTag?: string;
  service?: string;
  insBenefits?: {
    policyTitle: string;
    policyAmt: string;
  }[];
  otherInsurer?: null;
  moreBenefits?: {
    [key: string]: IBenefits[] | undefined;
  };
  isSelected?: boolean;
  sportsActivity?: {
    icon: string;
    title: TextNodeType;
  };
  travelAssistanceWarning?: {
    icon: string;
    title: TextNodeType;
    info: TextNodeType;
  };
  travelAssistanceCovers?: {
    [key: string]: IBenefits[] | undefined;
  };
  expertAssistanceInfo?: TextNodeType;
  assistanceCovers?: string[];
  expertAssistanceIcon?: string;
  totalBenefits?: string;
  image?: {
    url: string;
    height: string;
    width: string;
  };
}

export interface IPlanData {
  id: string;
  cardType?: "PLAN" | "BANNER";
  cardData?: CardDataInterface;
  productId: string;
  quoteId: string;
  productType?: string;
  insPlanName?: TextNodeType;
  packName?: TextNodeType;
  vendorDisplayIcon?: string;
  comparePlans?: TextNodeType;
  paxPerDay?: TextNodeType;
  insAmount?: TextNodeType;
  insIcon?: string;
  hasTravelAssistant?: boolean;
  provideCashlessClaim?: boolean;
  insuranceType?: TextNodeType;
  planExtraInfo?: TextNodeType;
  buyBtn?: string;
  originalPrice?: TextNodeType;
  discountText?: TextNodeType;
  selectedForCompare?: boolean;
  vendorDisplayPrefix?: string;
  insurerDetails?: {
    insurer: TextNodeType;
    details: TextNodeType;
  };
  insurerNumber?: TextNodeType;
  ctaLink?: string | TextNodeType;
  tncLink?: string;
  isPopular?: boolean;
  popularTag?: string;
  service?: string;
  insBenefits?: {
    policyTitle: string;
    policyAmt: string;
  }[];
  otherInsurer?: null;
  moreBenefits?: {
    [key: string]: IBenefits[] | undefined;
  };
  isSelected?: boolean;
  sportsActivity?: {
    icon: string;
    title: TextNodeType;
  };
  travelAssistanceWarning?: {
    icon: string;
    title: TextNodeType;
    info: TextNodeType;
  };
  travelAssistanceCovers?: {
    [key: string]: IBenefits[] | undefined;
  };
  expertAssistanceInfo?: TextNodeType;
  assistanceCovers?: string[];
  expertAssistanceIcon?: string;
  totalBenefits?: string;
}

export interface IFormFieldType {
  fieldName?: string;
  isRequired?: boolean;
  isEnabled?: boolean;
  regex?: string;
  title?: string;
  value?: string;
  values?: {
    id: string;
    label: string;
    isSelected: boolean;
  }[];
  placeholder?: string;
  errorMessage?: string;
  countryCode?: string;
  isEditable?: boolean;
}

export interface IBenefit {
  key?: string;
  name?: string;
  cover?: string;
  description?: string;
  deductible?: string;
  icon?: string;
}

export interface IBenefitsGroup {
  key: string;
  name?: string;
  features?: IBenefit[];
}

export interface IPlanBenefits {
  heading?: TextNodeType;
  category?: TextNodeType;
  title?: TextNodeType;
  description?: TextNodeType;
  benefits?: IBenefitsGroup[];
  subtitle?: TextNodeType;
  note?: TextNodeType;
  amount?: TextNodeType;
  unitType?: TextNodeType;
  unitPrice?: TextNodeType;
  tnc?: {
    title?: TextNodeType;
    url?: string;
  };
}

export interface IToastData {
  autoDismissDurationInMilliseconds?: boolean;
  iconUrl?: string;
  message?: TextNodeType;
  type?: string;
}
export interface IWidgetData {
  formErrorToast?: IToastData;
  addErrorToast?: IToastData;
  offerToast?: IToastData;
  persuasionBanner?: {
    title: TextNodeType;
    description?: TextNodeType;
    icon?: string;
  };
}
export interface IPrimaryDetails {
  title: TextNodeType;
  description?: TextNodeType;
  cta?: {
    value?: string;
    type?: string;
    url?: string;
    toggleValue?: string;
  };
}

export interface IComboProductDetails {
  productTitle: string;
  totalAmountToPay: string;
  originalAmountToPay: string;
  tncLink?: string;
}

export interface IAddOnPlans {
  key: number;
  planCode: string;
  productId?: string;
  quoteId: string;
  planIcon: string;
  primary: IPrimaryDetails;
  secondary: IPrimaryDetails;
  comboProductDetails: IComboProductDetails;
  addPlanEvent: "Adventure_added" | "Electronics_added";
  addedIcon?: string;
  isSelected?: boolean;
  tag?: TextNodeType;
  modal: IPlanData;
}

export interface IFilters {
  id: number;
  name?: string;
  plans?: string[];
  cardIds?: string[];
  isSelected?: boolean;
}
export interface IAddOns {
  title: TextNodeType;
  subTitle?: TextNodeType;
  experimentInfo: string;
  filters?: IFilters[];
  addOnPlans?: IAddOnPlans[];
}
export interface ITravellerDetailsResponseData {
  title: TextNodeType;
  isDomesticBooking?: boolean;
  experiment?: string;
  vendorDisplayIcon?: string;
  travellerPlanInfo?: {
    icon?: string;
    title?: TextNodeType;
    info?: TextNodeType;
  };
  planInfo?: {
    quoteId?: string;
    planType?: TextNodeType;
    planName?: TextNodeType;
    sumInsured?: TextNodeType;
    amountToPay?: TextNodeType;
    priceDetails?: {
      title: TextNodeType;
      originalPrice?: TextNodeType;
      discountedPrice?: TextNodeType;
    };
  };
  addOns?: IAddOns;
  travellerDetail?: {
    title: TextNodeType;
    info: TextNodeType;
  };
  primaryContact?: {
    title: TextNodeType;
    info: TextNodeType;
    fields?: {
      mobile?: IFormFieldType;
      email?: IFormFieldType;
    };
  };
  gstInfo?: {
    title: TextNodeType;
    fields?: {
      gstNumber?: IFormFieldType;
      companyDetails?: IFormFieldType;
    };
  };
  secondaryContact?: {
    title: string;
    fields?: {
      name?: IFormFieldType;
      dob?: IFormFieldType;
      gender?: IFormFieldType;
      passport?: IFormFieldType;
      nomineeName?: IFormFieldType;
    };
    isEditable?: boolean;
  };
  travelInfo?: {
    countries?: string;
    mode?: string;
    travellers?: number;
    travelDates?: {
      startDate?: Date;
      endDate?: Date;
    };
    travelPurpose?: string;
  };
  planBenefits?: IPlanBenefits;
  previewBenefits?: {
    benefits: IBenefitsGroup[];
    ctaText?: TextNodeType;
  };
  tnc?: {
    title?: TextNodeType;
    urls?: string;
  };
  note?: {
    title?: TextNodeType;
    info?: TextNodeType;
  };
  concent?: {
    tnc?: string[];
    link?: {
      title?: string;
      url?: string;
    };
    info?: TextNodeType;
  };
  totalAmt?: string;
  upcomingTravellerDetails?: {
    name: string;
    dob: string;
    gender: string;
    passport: string;
    nomineeName?: string;
  }[];
  savedTravellers?: {
    name?: string;
    dob?: string;
    gender?: string;
    passport?: string;
    isDefaultSelected?: boolean;
    nomineeName?: string;
  }[];
  travelDetails?: {
    countries?: string;
  };
  widgets?: IWidgetData;
  chatBotInfo?: {
    isEnabled: boolean;
    myraUrl: string;
    type: "MYRA" | "TRAVELPLEX" | undefined;
  };
}

export interface IPassportDetailsResponseData {
  heading?: TextNodeType;
  subHeading?: TextNodeType;
  planCardTitle?: TextNodeType;
  SelectedPlanData?: {
    planType?: TextNodeType;
    planName?: TextNodeType;
    payAmtTxt?: TextNodeType;
    payAmt?: TextNodeType;
    planTitle?: TextNodeType;
    countryTitle?: TextNodeType;
    travellerCount?: TextNodeType;
    travelPurpose?: TextNodeType;
    travelDate?: { startDate?: string; endDate?: string };
    vendorIcon?: string;
    vendorName?: TextNodeType;
    vendorNamePrefix: string;
    settleMentRatio?: TextNodeType;
  };
  addPassportTitle?: TextNodeType;
  addPassportInfo?: TextNodeType;
  submitCtaText?: string;
  skipCtaText?: string;
  travellerData?: {
    refId?: string;
    name?: string;
    Dob?: string;
    Gender?: string;
    passportNum?: string;
  }[];
  passport?: {
    isEnabled?: boolean;
    isRequired?: boolean;
    label?: string;
    placeHolder?: string;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    error_message?: string;
  };
  addPassportModalData?: {
    title?: TextNodeType;
    info?: TextNodeType;
    addBtnText?: string;
    skipBtnText?: string;
  };
  planBenefits?: {
    viewBenefitsCta?: TextNodeType;
    totalBenefits?: string;
    claimSettled?: TextNodeType;
    planBenefitsInfo?: string;
    featureGroup?: any;
    tnc: {
      title?: string;
      link?: string;
    };
  };
}
